import "moment-timezone";

import { EuiFlexGroup, EuiFlexItem, useEuiTheme } from "@elastic/eui";
import { EuiSuperSelectOption } from "@elastic/eui/src/components/form/super_select/super_select_control";
import { UseEuiTheme } from "@elastic/eui/src/services/theme/hooks";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useState } from "react";

import Accordion from "../../../components/accordion";
import DateTimePicker from "../../../components/dateTimePicker";
import Label from "../../../components/label";
import Select from "../../../components/select";
import {
  getUtcDateString,
  getUtcOffsetFromTimezoneName,
  isDateValid,
} from "../../../helpers/dateTime";
import {
  endDateValid,
  startDateValid,
} from "../../../helpers/workOrdersReactiveVariables";
import { ScheduleTypeEnum } from "../../../models/enums";
import { WorkOrder } from "../../../models/workOrder";
import WorkOrderTimeZoneSelector from "./timezoneSelector";

interface WorkOrderCreateServicesServiceSchedulePresenterProps {
  workOrder: WorkOrder;
  onEndDateSelected: (endDate: string) => void;
  onStartDateSelected: (endDate: string) => void;
  onScheduleTypeSelected: (scheduleType: ScheduleTypeEnum) => void;
  onTimezoneSelected: (timezone: string) => void;
}

const WorkOrderCreateServicesServiceSchedulePresenter: React.FC<
  WorkOrderCreateServicesServiceSchedulePresenterProps
> = ({
  workOrder,
  onEndDateSelected,
  onStartDateSelected,
  onScheduleTypeSelected,
  onTimezoneSelected,
}) => {
  const { euiTheme }: UseEuiTheme<{ sizes: any }> = useEuiTheme();
  const minSelectorWidth = "235px";
  const minValidDate = moment.utc();
  const recurrenceAvailable = false;
  const minValidDateError = "You cannot set up elapsed time";
  const scheduleTypeOptions: EuiSuperSelectOption<string>[] = [
    { value: ScheduleTypeEnum.OPEN_DUE, inputDisplay: "Open Due Date" },
    { value: ScheduleTypeEnum.STRICT_DUE, inputDisplay: "Strict Due Date" },
    { value: ScheduleTypeEnum.STRICT_TIME, inputDisplay: "Strict Time Frame" },
  ];
  const [isTimezoneSelectorVisible, setIsTimezoneSelectorVisible] =
    useState<boolean>(false);
  const [endDateMinValue, setEndDateMinValue] = useState<Moment>(moment());
  const [startDateMaxValue, setStartDateMaxValue] = useState<Moment | null>();
  const [startDateErrors, setStartDateErrors] = useState<string[]>([]);
  const [endDateErrors, setEndDateErrors] = useState<string[]>([]);

  useEffect(() => {
    setStartDateMaxValue(
      workOrder.endDateTime
        ? moment.utc(workOrder.endDateTime).subtract(1, "minutes")
        : null
    );
    const minDateFromStartDate = moment
      .utc(workOrder.startDateTime)
      .add(1, "minutes");
    setEndDateMinValue(
      workOrder.startDateTime &&
        minDateFromStartDate.valueOf() > minValidDate.valueOf()
        ? minDateFromStartDate
        : minValidDate
    );
    let newEndDateErrors = !workOrder.endDateTime
      ? []
      : isDateValid(workOrder.endDateTime, minValidDate)
      ? []
      : [minValidDateError];
    let newStartDateErrors = !workOrder.startDateTime
      ? []
      : isDateValid(moment.utc(workOrder.startDateTime), minValidDate)
      ? []
      : [minValidDateError];
    (newEndDateErrors.length || endDateErrors.length) &&
      setEndDateErrors(newEndDateErrors);
    (newStartDateErrors.length || startDateErrors.length) &&
      setStartDateErrors(newStartDateErrors);
  }, [workOrder.endDateTime, workOrder.startDateTime]);

  const _onError = useCallback(
    ({
      startError,
      endError,
    }: {
      startError?: string[];
      endError?: string[];
    }): void => {
      if (startError) {
        startDateValid(!startError.length);
      }
      if (endError) {
        endDateValid(!endError.length);
      }
    },
    [workOrder.endDateTime, workOrder.startDateTime]
  );

  return (
    <div>
      <Accordion
        id="wo-service-schedule"
        label="Service Schedule"
        required={true}
        initialIsOpen={true}
      >
        <EuiFlexGroup
          direction="column"
          justifyContent="spaceBetween"
          alignItems="stretch"
          wrap={false}
          gutterSize="none"
        >
          <EuiFlexItem
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: euiTheme.size.s,
            }}
          >
            <Label label="Schedule Type" required={true} />
            <div style={{ minWidth: minSelectorWidth }}>
              <Select
                selectedValue={workOrder.scheduleType}
                options={scheduleTypeOptions}
                onSelect={onScheduleTypeSelected}
                compressed={true}
              />
            </div>
          </EuiFlexItem>
          <EuiFlexItem
            style={{
              marginTop: isTimezoneSelectorVisible
                ? euiTheme.size.l
                : euiTheme.size.xs,
            }}
          >
            <WorkOrderTimeZoneSelector
              workOrder={workOrder}
              onIsSelectorVisibleChange={(value) =>
                setIsTimezoneSelectorVisible(value)
              }
              onTimezoneSelected={onTimezoneSelected}
            />
          </EuiFlexItem>
          {workOrder?.scheduleType === ScheduleTypeEnum.STRICT_TIME && (
            <EuiFlexItem
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: euiTheme.size.s,
                marginTop: euiTheme.size.l,
              }}
            >
              <Label label="Start Date" required={true} />
              <div
                style={{
                  minWidth: minSelectorWidth,
                  maxWidth: minSelectorWidth,
                }}
              >
                <DateTimePicker
                  selectedValue={workOrder.startDateTime}
                  utcOffset={getUtcOffsetFromTimezoneName(
                    workOrder.serviceScheduleTimezone
                  )}
                  minDate={minValidDate}
                  maxDate={startDateMaxValue}
                  required={true}
                  returnUtc={true}
                  placeholder="Start Date"
                  errors={startDateErrors}
                  invalid={!workOrder.startDateTime || !!startDateErrors.length}
                  onChange={onStartDateSelected}
                  onInternalErrors={(errors) =>
                    _onError({ startError: errors })
                  }
                />
              </div>
            </EuiFlexItem>
          )}
          <EuiFlexItem
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: euiTheme.size.s,
              marginTop: euiTheme.size.l,
            }}
          >
            <Label label="End Date" required={true} />
            <div
              style={{ minWidth: minSelectorWidth, maxWidth: minSelectorWidth }}
            >
              <DateTimePicker
                selectedValue={workOrder.endDateTime}
                utcOffset={getUtcOffsetFromTimezoneName(
                  workOrder.serviceScheduleTimezone
                )}
                minDate={endDateMinValue}
                required={true}
                returnUtc={true}
                errors={endDateErrors}
                invalid={!workOrder.endDateTime || !!endDateErrors.length}
                placeholder="End Date"
                onChange={onEndDateSelected}
                onInternalErrors={(errors) => _onError({ endError: errors })}
              />
            </div>
          </EuiFlexItem>
          {recurrenceAvailable ? (
            <EuiFlexItem
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: euiTheme.size.s,
                marginTop: euiTheme.size.l,
              }}
            >
              <Label label="Recurrence" disabled={true} />
              <div style={{ minWidth: minSelectorWidth }}>
                <Select
                  selectedValue={null}
                  disabled={true}
                  options={[]}
                  onSelect={() => {}}
                  compressed={true}
                />
              </div>
            </EuiFlexItem>
          ) : null}
        </EuiFlexGroup>
      </Accordion>
    </div>
  );
};

export default WorkOrderCreateServicesServiceSchedulePresenter;
